/**
 * Post-Signup Onboarding Wizard
 *
 * 4-step wizard that collects essential agent information after signup:
 *   Step 1: Select lead types of interest
 *   Step 2: Select licensed states
 *   Step 3: Contact & license details (phone, license number)
 *   Step 4: Review & confirm
 *
 * Updates the agent record (created by stripe-webhook) with the user's choices,
 * then redirects to /dashboard.
 */

import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Shield,
  ArrowRight,
  ArrowLeft,
  Heart,
  Home,
  Check,
  Loader2,
  MapPin,
  Sparkles,
  Search,
  Phone,
  Key,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/logo.png";

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

interface LeadTypeOption {
  id: string;
  name: string;
  description: string;
  icon: typeof Heart;
  iconColor: string;
  iconBg: string;
}

const LIFE_LEAD_TYPES: LeadTypeOption[] = [
  { id: "life_fex", name: "Final Expense", description: "Burial & funeral insurance leads", icon: Heart, iconColor: "text-rose-500", iconBg: "bg-rose-500/10" },
  { id: "life_iul", name: "Indexed Universal Life", description: "IUL & cash value life insurance leads", icon: Heart, iconColor: "text-purple-500", iconBg: "bg-purple-500/10" },
  { id: "life_mp", name: "Mortgage Protection", description: "Mortgage protection insurance leads", icon: Heart, iconColor: "text-amber-500", iconBg: "bg-amber-500/10" },
];

const MORTGAGE_LEAD_TYPES: LeadTypeOption[] = [
  { id: "mortgage_purchase", name: "Purchase", description: "Home purchase mortgage leads", icon: Home, iconColor: "text-blue-500", iconBg: "bg-blue-500/10" },
  { id: "mortgage_refi", name: "Refinance", description: "Refinance & cash-out leads", icon: Home, iconColor: "text-green-500", iconBg: "bg-green-500/10" },
  { id: "mortgage_heloc", name: "HELOC", description: "Home equity line of credit leads", icon: Home, iconColor: "text-cyan-500", iconBg: "bg-cyan-500/10" },
  { id: "mortgage_reverse", name: "Reverse Mortgage", description: "Reverse mortgage leads", icon: Home, iconColor: "text-indigo-500", iconBg: "bg-indigo-500/10" },
];

const TOTAL_STEPS = 4;

const Onboarding = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const { session, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const proposalId = searchParams.get("proposal_id");
  const [proposal, setProposal] = useState<any>(null);
  const [agent, setAgent] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(true);
  // Lead types that the agent's contract covers (empty = no restriction)
  const [contractedLeadTypes, setContractedLeadTypes] = useState<string[]>([]);

  const [selectedLeadTypes, setSelectedLeadTypes] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [licenseNumber, setLicenseNumber] = useState(() => {
    // Pre-fill from signup data if available
    const saved = JSON.parse(localStorage.getItem("tj_signup") || "null");
    return saved?.licenseNumber || "";
  });

  // Guard: if agent already completed onboarding, redirect to dashboard.
  // Uses SECURITY DEFINER RPC to handle ID mismatches (webhook-created agents
  // whose id doesn't match auth.uid() — RLS blocks direct SELECT in that case).
  useEffect(() => {
    if (!session || authLoading) return;

    const checkAlreadyOnboarded = async () => {
      // Clean up orphan agent records from previously deleted accounts.
      // This must run BEFORE resolve_agent_identity so the UNIQUE email
      // constraint doesn't block the migration.
      try {
        await (supabase.rpc as any)("cleanup_orphan_agent");
      } catch {
        // Function might not be deployed yet — graceful degradation
      }

      const { data: resolved, error } = await (supabase.rpc as any)("resolve_agent_identity");

      let alreadyOnboarded = false;

      if (!error && resolved && (resolved.resolved_by === 'id' || resolved.resolved_by === 'email_reconciled')) {
        if (Array.isArray(resolved.licensed_states) && resolved.licensed_states.length > 0) {
          alreadyOnboarded = true;
        }
      } else {
        // RPC not yet deployed — fallback to direct SELECT
        const { data: agent } = await supabase
          .from("agents")
          .select("licensed_states")
          .eq("id", session.user.id)
          .maybeSingle();

        if (agent?.licensed_states && (agent.licensed_states as string[]).length > 0) {
          alreadyOnboarded = true;
        }
      }

      if (alreadyOnboarded) {
        console.log("[Onboarding] Agent already onboarded, redirecting to dashboard");
        navigate("/dashboard", { replace: true });
      }
    };

    checkAlreadyOnboarded();
  }, [session, authLoading, navigate]);

  // Load proposal and agent data
  useEffect(() => {
    if (!session) return;

    const loadData = async () => {
      let resolvedAgent: any = null;

      // Load proposal if available
      if (proposalId) {
        const { data: proposalData } = await (supabase
          .from("contract_proposals" as any)
          .select("*")
          .eq("id", proposalId)
          .maybeSingle() as any);

        if (proposalData) {
          setProposal(proposalData);

          // Load agent from proposal
          if (proposalData.agent_id) {
            const { data: agentData } = await supabase
              .from("agents")
              .select("*")
              .eq("id", proposalData.agent_id)
              .maybeSingle();

            if (agentData) {
              resolvedAgent = agentData;
            }
          }
        }
      }

      // If no agent found from proposal, try to find by auth user id or email
      if (!resolvedAgent) {
        let { data: agentByAuth } = await supabase
          .from("agents")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();

        if (!agentByAuth && session.user.email) {
          const { data: agentByEmail } = await supabase
            .from("agents")
            .select("*")
            .eq("email", session.user.email)
            .maybeSingle();
          agentByAuth = agentByEmail;
        }

        if (agentByAuth) {
          resolvedAgent = agentByAuth;
        }
      }

      if (resolvedAgent) {
        setAgent(resolvedAgent);
        const prefs = (resolvedAgent.lead_preferences as any[]) || [];
        const activeTypes = prefs.filter((p) => p.isActive).map((p) => p.leadTypeId);
        if (activeTypes.length > 0) {
          setSelectedLeadTypes(activeTypes);
        }
        // Pre-fill phone and license if agent already has them
        if (resolvedAgent.phone) setPhoneNumber(resolvedAgent.phone);
        if (resolvedAgent.license_number) setLicenseNumber(resolvedAgent.license_number);
      }

      // Fetch contracted lead types to enforce restrictions
      const userEmail = session.user.email;
      if (userEmail) {
        const { data: agentContracts } = await supabase
          .from("contracts")
          .select("lead_types")
          .eq("agent_email", userEmail)
          .in("status", ["active", "at_risk"]);

        const allContractedTypes = [...new Set(
          (agentContracts || [])
            .flatMap((c: any) => Array.isArray(c.lead_types) ? c.lead_types : [])
        )];

        if (allContractedTypes.length > 0) {
          setContractedLeadTypes(allContractedTypes);
          // Merge contracted types with saved preferences instead of overwriting.
          // Contracted types are always included; agent's prior active selections are preserved.
          setSelectedLeadTypes((prev) => {
            const merged = new Set([...prev, ...allContractedTypes]);
            return [...merged];
          });
        }
      }

      setDataLoading(false);
    };

    loadData();
  }, [session, proposalId]);

  // Recover vertical from proposal, agent record, or localStorage signup data
  const signupData = useMemo(() => JSON.parse(localStorage.getItem("tj_signup") || "null"), []);
  const vertical = proposal?.vertical || agent?.vertical || signupData?.vertical || "life_insurance";
  const leadTypeOptions = vertical === "mortgage" ? MORTGAGE_LEAD_TYPES : LIFE_LEAD_TYPES;

  const filteredStates = useMemo(() => {
    if (!stateSearch) return US_STATES;
    const q = stateSearch.toLowerCase();
    return US_STATES.filter(
      (s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)
    );
  }, [stateSearch]);

  const toggleLeadType = (id: string) => {
    setSelectedLeadTypes((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const toggleState = (code: string) => {
    setSelectedStates((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]
    );
  };

  const handleSubmit = async () => {
    if (selectedLeadTypes.length === 0) {
      toast.error("Please select at least one lead type.");
      return;
    }
    if (selectedStates.length === 0) {
      toast.error("Please select at least one state.");
      return;
    }
    if (!phoneNumber.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }

    setIsSubmitting(true);

    try {
      const agentId = agent?.id || session?.user.id;
      if (!agentId) {
        toast.error("Agent not found. Please contact support.");
        setIsSubmitting(false);
        return;
      }

      // Update lead_preferences: set isActive based on user selection
      const currentPrefs = (agent?.lead_preferences as any[]) || [];
      let updatedPrefs: any[];

      if (currentPrefs.length > 0) {
        // Update existing preferences
        updatedPrefs = currentPrefs.map((pref: any) => ({
          ...pref,
          isActive: selectedLeadTypes.includes(pref.leadTypeId),
        }));
      } else {
        // Create preferences from scratch
        updatedPrefs = leadTypeOptions.map((lt) => ({
          leadTypeId: lt.id,
          leadTypeName: lt.name,
          vertical,
          basePrice: 30,
          bidValue: 35,
          dailyCap: 10,
          monthlyCap: 200,
          isActive: selectedLeadTypes.includes(lt.id),
        }));
      }

      // Use upsert so it works whether agent record exists or not
      // Recover signup data from localStorage (saved during signup form)
      const signupData = JSON.parse(localStorage.getItem("tj_signup") || "null");
      const agentName = agent?.name
        || (signupData?.firstName && signupData?.lastName
            ? `${signupData.firstName} ${signupData.lastName}`
            : null)
        || session?.user.user_metadata?.full_name
        || session?.user.email?.split("@")[0]
        || "Agent";
      const agentCompany = agent?.company || signupData?.company || null;

      const agentPayload = {
        id: agentId,
        name: agentName,
        email: agent?.email || session?.user.email || "",
        company: agentCompany,
        vertical: vertical,
        tier: agent?.tier || "Silver",
        licensed_states: selectedStates,
        lead_preferences: updatedPrefs,
        phone: phoneNumber.trim() || null,
        license_number: licenseNumber.trim() || null,
        is_active: true,
      };

      console.log("[Onboarding] Saving agent:", { agentId, statesCount: selectedStates.length });

      const { error } = await supabase
        .from("agents")
        .upsert(agentPayload, { onConflict: "id" });

      if (error) {
        console.error("[Onboarding] Upsert error:", error);

        // If upsert fails (e.g. UNIQUE constraint on email from a stale row),
        // try an explicit UPDATE as fallback
        console.log("[Onboarding] Trying explicit UPDATE as fallback...");
        const { error: updateError } = await supabase
          .from("agents")
          .update({
            name: agentName,
            licensed_states: selectedStates,
            lead_preferences: updatedPrefs,
            phone: phoneNumber.trim() || null,
            license_number: licenseNumber.trim() || null,
            is_active: true,
          })
          .eq("id", agentId);

        if (updateError) {
          console.error("[Onboarding] Fallback UPDATE also failed:", updateError);
          toast.error("Failed to save preferences. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }

      // Immediately verify the write succeeded (silent RLS failures return no error)
      const { data: verifyData } = await supabase
        .from("agents")
        .select("id, licensed_states")
        .eq("id", agentId)
        .maybeSingle();

      if (!verifyData || !verifyData.licensed_states || (verifyData.licensed_states as string[]).length === 0) {
        console.error("[Onboarding] CRITICAL: Agent record not found after upsert!", {
          agentId,
          verifyData,
          sessionUserId: session?.user.id,
        });
        toast.error("Failed to save your profile. Please sign out and sign in again.");
        setIsSubmitting(false);
        return;
      }

      console.log("[Onboarding] Agent saved and verified:", verifyData.id);

      // Link proposal to this agent first (so the proposals bridge works for claiming)
      if (proposalId) {
        await supabase
          .from("contract_proposals" as any)
          .update({ agent_id: agentId, auth_user_id: session?.user.id } as any)
          .eq("id", proposalId);
        console.log("[Onboarding] Linked proposal to agent:", proposalId);
      }

      // Try RPC claim first (SECURITY DEFINER, handles email mismatches)
      let claimed = false;
      try {
        const { data: rpcResult, error: rpcError } = await (supabase.rpc as any)(
          "claim_agent_contracts"
        );
        if (!rpcError && rpcResult) {
          console.log("[Onboarding] RPC claim result:", rpcResult);
          claimed = true;
        }
      } catch {
        // Function might not exist yet — graceful degradation
      }

      // Fallback: claim contracts by direct email match + proposal bridge
      if (!claimed) {
        const { error: claimError } = await supabase
          .from("contracts")
          .update({ agent_id: agentId } as any)
          .eq("agent_email", session?.user.email ?? "")
          .is("agent_id", null);

        if (claimError) {
          console.warn("[Onboarding] Failed to claim contracts by email:", claimError);
        } else {
          console.log("[Onboarding] Claimed contracts for", session?.user.email);
        }

        // Also try claiming via proposal's contract_id (handles email mismatch)
        if (proposalId) {
          const { data: propData } = await (supabase
            .from("contract_proposals" as any)
            .select("contract_id")
            .eq("id", proposalId)
            .maybeSingle() as any);

          if (propData?.contract_id) {
            await supabase
              .from("contracts")
              .update({ agent_id: agentId, agent_email: session?.user.email } as any)
              .eq("id", propData.contract_id)
              .is("agent_id", null);
            console.log("[Onboarding] Claimed contract via proposal:", propData.contract_id);
          }
        }
      }

      // Clean up signup data and loop-detection counter
      localStorage.removeItem("tj_signup");
      sessionStorage.removeItem("tj_onboarding_redirects");

      toast.success("You're all set! Welcome to Trusted James.");

      // Force a full page reload to ensure all contexts (AgentContext, etc.) re-initialize
      // with fresh data from the database. SPA navigation (navigate()) would keep stale state.
      console.log("[Onboarding] Success — redirecting to dashboard via full reload");
      window.location.href = "/dashboard";
    } catch (err) {
      console.error("Onboarding error:", err);
      toast.error("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-lg py-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img src={logo} alt="Trusted James" className="h-10 w-auto" />
          </Link>

          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mb-8">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full ${step >= i + 1 ? "bg-accent" : "bg-muted"}`}
              />
            ))}
          </div>

          {/* Step 1: Lead Types */}
          {step === 1 && (
            <>
              <div className="mb-8">
                <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                  Select your lead types
                </h1>
                <p className="text-muted-foreground">
                  {contractedLeadTypes.length > 0
                    ? "Your contract includes the lead types pre-selected below. Others are not included."
                    : "Choose which types of leads you'd like to receive. You can change this later."}
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {leadTypeOptions.map((lt) => {
                  const Icon = lt.icon;
                  const isSelected = selectedLeadTypes.includes(lt.id);
                  const hasContractRestriction = contractedLeadTypes.length > 0;
                  const isContracted = contractedLeadTypes.includes(lt.id);
                  const isDisabled = hasContractRestriction && !isContracted;
                  return (
                    <button
                      key={lt.id}
                      type="button"
                      onClick={() => !isDisabled && toggleLeadType(lt.id)}
                      disabled={isDisabled}
                      className={`relative w-full p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                        isDisabled
                          ? "border-border/30 bg-muted/20 opacity-50 cursor-not-allowed"
                          : isSelected
                          ? "border-accent bg-accent/5"
                          : "border-border hover:border-accent/50"
                      }`}
                    >
                      {isSelected && !isDisabled && (
                        <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                          <Check className="w-4 h-4 text-accent-foreground" />
                        </div>
                      )}
                      {isDisabled && (
                        <div className="absolute top-4 right-4 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                          Not in contract
                        </div>
                      )}
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl ${lt.iconBg} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${lt.iconColor}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{lt.name}</h3>
                          <p className="text-sm text-muted-foreground">{lt.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button
                variant="gold"
                size="lg"
                className="w-full"
                disabled={selectedLeadTypes.length === 0}
                onClick={() => setStep(2)}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}

          {/* Step 2: Licensed States */}
          {step === 2 && (
            <>
              <div className="mb-6">
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-accent hover:underline mb-4 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to lead types
                </button>
                <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                  Licensed states
                </h1>
                <p className="text-muted-foreground">
                  Select the states where you're licensed. You'll only receive leads from these states.
                </p>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search states..."
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  className="h-10 pl-10"
                />
              </div>

              {/* Selected count */}
              {selectedStates.length > 0 && (
                <p className="text-sm text-accent font-medium mb-3">
                  {selectedStates.length} state{selectedStates.length !== 1 ? "s" : ""} selected
                </p>
              )}

              {/* States grid */}
              <div className="grid grid-cols-3 gap-2 mb-8 max-h-[400px] overflow-y-auto pr-1">
                {filteredStates.map((s) => {
                  const isSelected = selectedStates.includes(s.code);
                  return (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => toggleState(s.code)}
                      className={`p-2.5 rounded-xl border text-left transition-all duration-150 ${
                        isSelected
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border hover:border-accent/50 text-foreground"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span className="text-sm font-medium">{s.code}</span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{s.name}</p>
                    </button>
                  );
                })}
              </div>

              <Button
                variant="gold"
                size="lg"
                className="w-full"
                disabled={selectedStates.length === 0}
                onClick={() => setStep(3)}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}

          {/* Step 3: Contact & License */}
          {step === 3 && (
            <>
              <div className="mb-6">
                <button
                  onClick={() => setStep(2)}
                  className="text-sm text-accent hover:underline mb-4 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to states
                </button>
                <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                  Contact & license
                </h1>
                <p className="text-muted-foreground">
                  We'll use your phone number to deliver leads and send notifications.
                </p>
              </div>

              <div className="space-y-5 mb-8">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-accent" />
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="h-12 text-base"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <Key className="w-4 h-4 text-accent" />
                    License Number
                    <span className="text-xs text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <Input
                    placeholder="e.g. NPN 12345678"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    className="h-12 text-base"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Your primary insurance or mortgage license number. You can add it later in Settings.
                  </p>
                </div>
              </div>

              <Button
                variant="gold"
                size="lg"
                className="w-full"
                disabled={!phoneNumber.trim()}
                onClick={() => setStep(4)}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <>
              <div className="mb-6">
                <button
                  onClick={() => setStep(3)}
                  className="text-sm text-accent hover:underline mb-4 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to contact details
                </button>
                <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                  You're almost ready!
                </h1>
                <p className="text-muted-foreground">
                  Review your selections and start receiving leads.
                </p>
              </div>

              <div className="space-y-4 mb-8">
                {/* Contract summary */}
                {proposal && (
                  <div className="rounded-xl border bg-card p-4 space-y-2">
                    <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-accent" />
                      Your Contract
                    </h3>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Package</span>
                      <span className="font-medium">{proposal.total_leads} leads</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Amount</span>
                      <span className="font-medium">${proposal.total_amount}</span>
                    </div>
                  </div>
                )}

                {/* Lead types summary */}
                <div className="rounded-xl border bg-card p-4 space-y-2">
                  <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <Heart className="w-4 h-4 text-accent" />
                    Lead Types
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedLeadTypes.map((id) => {
                      const lt = leadTypeOptions.find((l) => l.id === id);
                      return lt ? (
                        <span
                          key={id}
                          className="inline-flex items-center px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium"
                        >
                          {lt.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* States summary */}
                <div className="rounded-xl border bg-card p-4 space-y-2">
                  <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-accent" />
                    Licensed States ({selectedStates.length})
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedStates.sort().map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-muted text-foreground text-sm"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Contact & License summary */}
                <div className="rounded-xl border bg-card p-4 space-y-2">
                  <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4 text-accent" />
                    Contact & License
                  </h3>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Phone</span>
                    <span className="font-medium">{phoneNumber}</span>
                  </div>
                  {licenseNumber.trim() && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">License #</span>
                      <span className="font-medium">{licenseNumber}</span>
                    </div>
                  )}
                </div>
              </div>

              <Button
                variant="gold"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Start Receiving Leads
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="hidden lg:flex flex-1 gradient-hero items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-10 w-72 h-72 bg-accent rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center max-w-md">
          <div className="w-24 h-24 rounded-3xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center mx-auto mb-8">
            <Shield className="w-12 h-12 text-accent" />
          </div>
          <h2 className="font-display text-3xl font-bold text-primary-foreground mb-4">
            Almost there!
          </h2>
          <p className="text-primary-foreground/70 leading-relaxed mb-8">
            Just a few more details and you'll start receiving exclusive, high-quality leads.
          </p>

          <div className="space-y-4">
            {[
              { label: "Choose your lead types", stepNum: 1 },
              { label: "Select licensed states", stepNum: 2 },
              { label: "Contact & license", stepNum: 3 },
              { label: "Review & start", stepNum: 4 },
            ].map(({ label, stepNum }) => (
              <div
                key={stepNum}
                className={`flex items-center gap-3 text-left rounded-xl p-4 border transition-all duration-300 ${
                  step >= stepNum ? "bg-accent/10 border-accent/30" : "bg-primary-foreground/5 border-primary-foreground/10"
                }`}
              >
                {step > stepNum ? (
                  <Check className="w-5 h-5 text-accent flex-shrink-0" />
                ) : (
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    step >= stepNum
                      ? "border-accent text-accent"
                      : "border-primary-foreground/30 text-primary-foreground/50"
                  }`}>
                    {stepNum}
                  </span>
                )}
                <span className={step >= stepNum ? "text-primary-foreground/90 text-sm" : "text-primary-foreground/60 text-sm"}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
